import { useCallback, useEffect, useMemo } from 'react';
import { ShopListWithScroll } from '~/components/ui/ShopListWithScroll';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, VehicleTuningCategory, WalletType } from '~/shared';
import { useBuy } from '../../hooks';
import { VEHICLE_TUNING_SHOP_CATEGORIES } from '~/layouts/tuningStudio/tuningGroups.model';
import {
	setActiveList,
	setActiveListIndex,
	setActiveStage,
	setActiveVariant,
	setMaxListIndex,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { getEffectiveTuningSelection } from '../../utils/resolveTuningShopVariant';
import { CATEGORY_ICONS } from '../CategoryTab';
import { ListTabItem } from '../ListTabItem';

const ListTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const activeListIndex = useLayoutSelector(
		(state) => state.tuning.activeListIndex,
	);
	const maxListIndex = useLayoutSelector((state) => state.tuning.maxListIndex);
	const activeCategory = useLayoutSelector(
		(state) => state.tuning.activeCategory,
	);
	const shopItems = useLayoutSelector((state) => state.tuning.shopItems);
	const shopCategories = VEHICLE_TUNING_SHOP_CATEGORIES;
	const modalState = useObservable(rage.modal.getCurrentModal());
	const activeVariants = useLayoutSelector(
		(state) => state.tuning.activeVariants,
	);
	const { handleBuy } = useBuy();

	const filteredItems = useMemo(
		() => shopItems.filter((item) => item.categoryId === activeCategory),
		[shopItems, activeCategory],
	);

	const handleCheckInstall = useCallback(
		(index: number): boolean => {
			const item = filteredItems[index];
			if (!item) return false;
			const effective = getEffectiveTuningSelection(item, activeVariants);
			return effective.isInstalled;
		},
		[filteredItems, activeVariants],
	);

	const handleSelectItem = useCallback((index: number | null) => {
		dispatch(setActiveListIndex(index));
	}, []);

	const handleBack = useCallback(() => {
		dispatch(setActiveStage('category'));
	}, []);

	const getItemId = useCallback(
		(index: number) => {
			const item = filteredItems?.[index];
			if (!item) return '';
			const effective = getEffectiveTuningSelection(item, activeVariants);
			return `tuning-${effective.id}`;
		},
		[filteredItems, activeVariants],
	);

	useEffect(() => {
		if (activeListIndex === null) return;
		const item = filteredItems[activeListIndex];
		if (!item?.variants?.length) return;
		if (activeVariants[item.id] === undefined) {
			dispatch(setActiveVariant({ itemId: item.id, index: 0 }));
		}
	}, [activeListIndex, filteredItems, activeVariants, dispatch]);

	useInputAction(InputAction.CommonMenuNavLeft, () => {
		if (modalState || activeListIndex === null) return;
		const item = filteredItems[activeListIndex];
		if (!item?.variants?.length) return;
		const cur = activeVariants[item.id] ?? 0;
		const next = cur === 0 ? item.variants.length - 1 : cur - 1;
		dispatch(setActiveVariant({ itemId: item.id, index: next }));
	});

	useInputAction(InputAction.CommonMenuNavRight, () => {
		if (modalState || activeListIndex === null) return;
		const item = filteredItems[activeListIndex];
		if (!item?.variants?.length) return;
		const cur = activeVariants[item.id] ?? 0;
		const next = cur === item.variants.length - 1 ? 0 : cur + 1;
		dispatch(setActiveVariant({ itemId: item.id, index: next }));
	});

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (activeListIndex === null || modalState) {
			return;
		}

		const activeItem = filteredItems[activeListIndex];
		if (activeItem?.isInstalled) {
			return;
		}

		handleBuy(WalletType.Cash);
	});

	useEffect(() => {
		if (activeListIndex === null) {
			return;
		}

		const activeItem = filteredItems[activeListIndex];
		if (activeItem) {
			if (activeItem.categoryId === VehicleTuningCategory.KITS) {
				rage.vehicleTuningShop
					.installItemsKit(activeItem.id)
					.catch((err) => rage.notifications.showException(err));
			} else {
				const effective = getEffectiveTuningSelection(
					activeItem,
					activeVariants,
				);
				rage.vehicleTuningShop
					.installItem(effective.id)
					.catch((err) => rage.notifications.showException(err));
			}
		}
	}, [filteredItems, activeListIndex, activeVariants]);

	useEffect(() => {
		dispatch(setMaxListIndex(filteredItems.length - 1));
		dispatch(setActiveList(filteredItems));
	}, [filteredItems]);

	const categoryTitle = useMemo(
		() =>
			shopCategories.find((e) => e.id === activeCategory)?.name ??
			'Неизвестная категория',
		[activeCategory, shopCategories],
	);

	if (!activeCategory) {
		return null;
	}

	return (
		<ShopListWithScroll
			icon={CATEGORY_ICONS[activeCategory]}
			title={categoryTitle}
			style={{ marginTop: 30 }}
			handleBack={handleBack}
			activeIndex={activeListIndex}
			maxIndex={maxListIndex}
			handleCheckInstall={handleCheckInstall}
			handleSelect={handleSelectItem}
			height={567}
			allowSelectInstalled
			enableAutoScroll
			getItemId={getItemId}
		>
			{filteredItems.map((item, index) => (
				<ListTabItem
					item={item}
					index={index}
					activeCategory={activeCategory}
					activeListIndex={activeListIndex}
					handleSelect={handleSelectItem}
					variantIndex={
						item.variants?.length
							? Math.min(
									Math.max(0, activeVariants[item.id] ?? 0),
									item.variants.length - 1,
							  )
							: undefined
					}
					key={index}
				/>
			))}
		</ShopListWithScroll>
	);
};

export { ListTab };
